import { exportDatasetJson } from "../export/json.ts";
import type { Dataset } from "../domain/types.ts";
import { BACKUP_STORE_NAME, openDb } from "./db.ts";
import { retainedBackups, type BackupRecord, type RetainedBackup } from "./backupRetention.ts";

function request<T>(operation: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    operation.onsuccess = () => resolve(operation.result);
    operation.onerror = () => reject(operation.error);
  });
}

function transactionDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(tx.error ?? new Error("Backup transaction was aborted"));
    tx.onerror = () => reject(tx.error ?? new Error("Backup transaction failed"));
  });
}

/** Writes the new checkpoint and prunes old ones in one transaction. */
export async function captureSessionBackup(dataset: Dataset, now = new Date()): Promise<void> {
  const record: BackupRecord = {
    id: `${now.toISOString()}-${crypto.randomUUID()}`,
    createdAt: now.toISOString(),
    json: exportDatasetJson(dataset),
  };
  const db = await openDb();
  try {
    const tx = db.transaction(BACKUP_STORE_NAME, "readwrite");
    const done = transactionDone(tx);
    const objectStore = tx.objectStore(BACKUP_STORE_NAME);
    try {
      const existing = await request(objectStore.getAll()) as BackupRecord[];
      const kept = retainedBackups([...existing, record]);
      const keepIds = new Set(kept.map((backup) => backup.id));
      objectStore.put(record);
      for (const old of existing) {
        if (!keepIds.has(old.id)) objectStore.delete(old.id);
      }
      await done;
    } catch (cause) {
      // If the read fails, wait for the abort too, so it cannot become an
      // unhandled rejection while the startup error is being reported.
      try { tx.abort(); } catch { /* Already committed or aborted. */ }
      await done.catch(() => undefined);
      throw cause;
    }
  } finally {
    db.close();
  }
}

export async function listSessionBackups(): Promise<RetainedBackup[]> {
  const db = await openDb();
  try {
    const tx = db.transaction(BACKUP_STORE_NAME, "readonly");
    const all = await request(tx.objectStore(BACKUP_STORE_NAME).getAll()) as BackupRecord[];
    return retainedBackups(all);
  } finally {
    db.close();
  }
}

export async function readSessionBackup(id: string): Promise<BackupRecord> {
  const db = await openDb();
  try {
    const tx = db.transaction(BACKUP_STORE_NAME, "readonly");
    const found = await request(tx.objectStore(BACKUP_STORE_NAME).get(id)) as BackupRecord | undefined;
    if (!found) throw new Error("This backup is no longer available. Refresh the list and try again.");
    return found;
  } finally {
    db.close();
  }
}
